import { AnalysisResult } from "../contracts/scoring";
import { computeSHA256 } from "../utils/hashUtils";
import { stableStringify } from "../utils/jsonUtils";

/**
 * GSC CERTIFICATION PROTOCOL v2.0 - NO-SIM ANCHORING
 * Authority: Senior Reliability Engineer
 */

export type UIConsumptionModel = Readonly<AnalysisResult> & {
  __certified: boolean;
  __isSealValid: boolean;
};

/**
 * ✅ TS-SAFE ArrayBuffer conversion
 * TextEncoder -> Uint8Array, mais TS peut typer u8.buffer en ArrayBuffer | SharedArrayBuffer.
 * On force un vrai ArrayBuffer en copiant les bytes dans un nouveau Uint8Array.
 */
function u8ToArrayBuffer(u8: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(u8.byteLength);
  copy.set(u8);
  return copy.buffer;
}

async function generateLocalSeal(result: AnalysisResult): Promise<string> {
  // On scelle le score ET les preuves physiques pour empêcher la triche en mémoire
  const sealPayload = {
    score: result.session.score,
    metrics: {
      rhythm: result.session.metrics.rhythm,
      harmony: result.session.metrics.harmony,
      cleanliness: result.session.metrics.cleanliness,
      // Preuves physiques immuables
      rms: result.session.metrics.debug?.rms_db,
      onsets: result.session.metrics.debug?.onset_count,
    },
    audio_hash: result.audit_trail.audio_hash,
    engine_v: result.audit_trail.engine_version,
  };

  const bytes = new TextEncoder().encode(stableStringify(sealPayload));
  return await computeSHA256(u8ToArrayBuffer(bytes));
}

export async function sealResult(result: AnalysisResult): Promise<AnalysisResult> {
  const seal = await generateLocalSeal(result);

  const bytes2 = new TextEncoder().encode(stableStringify(result.session.metrics));
  const analysisHash = await computeSHA256(u8ToArrayBuffer(bytes2));

  return {
    ...result,
    audit_trail: {
      ...result.audit_trail,
      local_seal: seal,
      analysis_hash: analysisHash,
      computed_at: new Date().toISOString(),
    },
  };
}

export async function verifySealedScore(result: AnalysisResult): Promise<boolean> {
  if (!result.audit_trail?.local_seal) return false;
  const computedSeal = await generateLocalSeal(result);
  return computedSeal === result.audit_trail.local_seal;
}

export async function toUIModel(result: AnalysisResult): Promise<UIConsumptionModel> {
  const isSealValid = await verifySealedScore(result);
  const isCertified = isSealValid && result.session.certified === true;

  return {
    ...result,
    __certified: isCertified,
    __isSealValid: isSealValid,
  };
}
